import { UploadOutlined } from '@ant-design/icons';
import {
  Button,
  DatePicker,
  Flex,
  Form,
  Input,
  Space,
  message,
  Upload,
  UploadFile,
  Radio,
  InputNumber,
  Select,
} from 'antd';
import { UploadChangeParam } from 'antd/es/upload';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { uploadImage } from '@/api/common';
import ImageCropper from '@/components/ImageCropper';
import {
  activityNotificationCancel,
  activityNotificationSaveOrUpdate,
  activityNotificationDetail,
  ActivityNotificationSaveOrUpdateParams,
  activityNotificationTest,
  ActivityNotificationTestParams,
  ActivityPublishStatus,
  ActivityNotifyType,
  activityNotificationRecall,
} from '@/api/activityMng';
import dayjs, { Dayjs } from 'dayjs';
import { FORMAT_TIME } from '@/types/common';
import { useQuery } from '@tanstack/react-query';
import CancelConfirmDialog from './cancelConfirmDialog';
import {
  createArtworkDetailLink,
  createBrowserLink,
  createTagDetailLink,
  createWebviewLink,
  getValueFromLink,
  UniversalLinkPage,
  createContestDetailLink,
  createNewcomerContestLink,
} from '@/utils/universalLinks';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);


export enum UserEnum {
  '全部用户',
  '首登新用户',
  '7天内未登录用户' = 2,
  // '30天内未登录用户',
  '周活用户' = 4,
  '未参与过活动的用户' = 5,
  'iOS' = 7,
  'Android' = 8,
}

const userArr = Object.keys(UserEnum).filter((item) => isNaN(Number(item)));

// 文件大小限制配置 (MB)
const MAX_FILE_SIZE_MB = 1;

const ActivityEditorPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const [messageApi, contextHolder] = message.useMessage();
  const [form] = Form.useForm<{
    cover: string; // 改为字符串类型，存储图片 URL
    title: string;
    link: string;
    publishTime: Dayjs;
  }>();

  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [testUser, setTestUser] = useState<string>('');
  const [isEdit, setIsEdit] = useState(false);
  const [hasUploadedNewImage, setHasUploadedNewImage] = useState(false); // 标记是否已上传新图片
  const hasUploadedNewImageRef = useRef(false); // ref 版本，供 useEffect 内读取最新值
  const [newImageRatio, setNewImageRatio] = useState<number | null>(null); // 新上传图片的 ratio
  const [isLoading, setIsLoading] = useState(false);
  const [isCancelConfirmDialogOpen, setIsCancelConfirmDialogOpen] =
    useState(false);
  const [linkType, setLinkType] = useState<ActivityNotifyType>(
    ActivityNotifyType.LINK
  );

  const timer = useRef();
  const [recallCountdown, setRecallCountdown] = useState<number>(0);

  // 如果有 id，则设置为编辑模式
  useEffect(() => {
    if (id) {
      setIsEdit(true);
    }
  }, [id]);

  const { data: activityNotification, refetch: refetchActivityNotification } =
    useQuery({
      queryKey: ['activityNotificationDetail', id],
      queryFn: async () => {
        const resp = await activityNotificationDetail(id || '');
        if (resp.status !== 200) {
          return null;
        }
        return resp.data;
      },
      enabled: !!id,
      refetchOnWindowFocus: false,
    });

  useEffect(() => {
    if (activityNotification) {
      // 获取链接值
      let universalLinkPage: UniversalLinkPage = UniversalLinkPage.webview;
      if (activityNotification.notifyType === ActivityNotifyType.TAG) {
        universalLinkPage = UniversalLinkPage.tagDetail;
      }
      if (activityNotification.notifyType === ActivityNotifyType.ARTWORK) {
        universalLinkPage = UniversalLinkPage.artworkDetail;
      }
      if (activityNotification.notifyType === ActivityNotifyType.CONTEST) {
        universalLinkPage = UniversalLinkPage.contestDetail;
      }
      if (activityNotification.notifyType === ActivityNotifyType.NEWCOMER_CONTEST) {
        universalLinkPage = UniversalLinkPage.rookieKingDetail;
      }
      const linkValue = getValueFromLink(
        activityNotification.link,
        universalLinkPage
      );

      const imageName = activityNotification.imageUrl.split('/').pop();
      const imageItem: UploadFile = {
        uid: '1',
        name: imageName || '',
        url: activityNotification.imageUrl,
      };
      // 只在没有上传新图片时才设置详情图片，避免覆盖用户刚裁剪上传的新图片
      if (!hasUploadedNewImageRef.current) {
        form.setFieldsValue({
          cover: activityNotification.imageUrl,
          title: activityNotification.title,
          link: linkValue || '',
          publishTime: dayjs(activityNotification.publishTime),
          pushTargetType: activityNotification.pushTargetType,
        });
      } else {
        // 如果已经上传了新图片，只更新其他字段，不更新 cover
        form.setFieldsValue({
          title: activityNotification.title,
          link: linkValue || '',
          publishTime: dayjs(activityNotification.publishTime),
          pushTargetType: activityNotification.pushTargetType,
        });
      }

      setFileList([imageItem]);
      setLinkType(activityNotification.notifyType || ActivityNotifyType.LINK);

      const publishTime = dayjs.tz(
        activityNotification.publishTime,
        'Asia/Tokyo'
      );
      const expirationTime = publishTime.add(1, 'day');

      const nowTime = dayjs.tz(new Date(), 'Asia/Tokyo');
      const diffhour = expirationTime.diff(nowTime, 'hour');
      if (diffhour > 0 && diffhour < 24) {
        const diff = expirationTime.diff(nowTime);
        setRecallCountdown(diff);
        timer.current = setInterval(() => {
          setRecallCountdown((time) => {
            const newtime = time - 1000;
            if (newtime <= 0) {
              clearInterval(timer.current);
            }

            return newtime;
          });
        }, 1000);
      }
    }

    return () => {
      if (timer.current) {
        clearInterval(timer.current);
      }
    };
  }, [form, activityNotification]);

  const handleSubmit = async () => {
    // 先进行表单校验
    try {
      await form.validateFields();
    } catch (error) {
      messageApi.error('请检查表单填写是否完整');
      return;
    }
    
    const values = form.getFieldsValue();
    // 优先使用表单字段中的 cover 值（如果用户更新了图片，这里会是新图片的 URL）
    // 如果没有更新，则使用详情数据中的 imageUrl
    const imageUrl = values.cover || activityNotification?.imageUrl;
    
    if (!imageUrl) {
      messageApi.error('请上传封面');
      return;
    }
    setIsLoading(true);
    // 如果上传了新图片，使用新图片的 ratio；否则使用详情数据中的 ratio
    const ratio = newImageRatio !== null ? newImageRatio : (activityNotification?.ratio || 0);
    const params: ActivityNotificationSaveOrUpdateParams = {
      activityNotificationId: id ? Number(id) : undefined,
      imageUrl,
      ratio,
      title: values.title,
      notifyType: linkType,
      link: handleSerializeLink(values.link),
      publishTime: dayjs(values.publishTime).format(FORMAT_TIME),
      pushTargetType: values.pushTargetType as number,
    };

    const resp = await activityNotificationSaveOrUpdate(params);
    setIsLoading(false);
    if (resp.status !== 200) {
      messageApi.error(`${id ? '更新' : '创建'}活动通知失败: ${resp.message}`);
      return;
    }
    messageApi.success(`${id ? '更新' : '创建'}活动通知成功`);
    setTimeout(() => {
      navigate('/activity-mng');
    }, 3000);
  };

  const handleSendTest = async () => {
    const values = form.getFieldsValue();
    // 优先使用表单字段中的 cover 值（如果用户更新了图片，这里会是新图片的 URL）
    // 如果没有更新，则使用详情数据中的 imageUrl
    const imageUrl = values.cover || activityNotification?.imageUrl;
    const ratio = newImageRatio !== null ? newImageRatio : (activityNotification?.ratio || 0);
    const params: ActivityNotificationTestParams = {
      imageUrl,
      ratio,
      title: values.title,
      link: handleSerializeLink(values.link),
      email: testUser,
      notifyType: linkType,
    };
    const resp = await activityNotificationTest(params);
    if (resp.status !== 200) {
      messageApi.error('发送测试失败: ' + resp.message);
      return;
    }
    messageApi.success('发送测试成功');
  };

  const handleSerializeLink = (link: string) => {
    if (linkType === ActivityNotifyType.LINK) {
      return createWebviewLink(link);
    }
    if (linkType === ActivityNotifyType.TAG) {
      return createTagDetailLink(link);
    }
    if (linkType === ActivityNotifyType.ARTWORK) {
      return createArtworkDetailLink(link);
    }
    if (linkType === ActivityNotifyType.BROWSER) {
      return createBrowserLink(link);
    }
    if (linkType === ActivityNotifyType.CONTEST) {
      return createContestDetailLink(link);
    }
    if (linkType === ActivityNotifyType.NEWCOMER_CONTEST) {
      return createNewcomerContestLink(link);
    }
    return link;
  };

  // 适配 uploadImage API 的上传函数
  // const handleImageUpload = async (file: File): Promise<any> => {
  //   const resp = await uploadImage(file);
  //   if (resp.status !== 200) {
  //     throw new Error(resp.message || '上传失败');
  //   }
  //   // 返回 ImageCropper 期望的格式
  //   return {
  //     status: 200,
  //     data: resp.data.imageUrl, // ImageCropper 期望 data 是字符串 URL
  //   };
  // };

  // 处理图片上传成功
  const handleImageUploadSuccess = useCallback((url: string, ratio?: number) => {
    // 更新表单字段，确保校验通过
    form.setFieldValue('cover', url);
    // 触发表单字段校验，清除可能的错误状态
    form.validateFields(['cover']).catch(() => {
      // 忽略校验错误，因为我们已经设置了值
    });
    setHasUploadedNewImage(true); // 标记已上传新图片，防止详情数据覆盖
    hasUploadedNewImageRef.current = true; // 同步更新 ref
    if (ratio !== undefined && ratio !== null) {
      setNewImageRatio(ratio); // 保存新图片的 ratio
    }
    setIsEdit(true);
  }, [form]);

  const handleCancel = async () => {
    if (!id) {
      return;
    }
    const resp = await activityNotificationCancel(Number(id));
    if (resp.status !== 200) {
      messageApi.error('取消发布失败: ' + resp.message);
      return;
    }
    messageApi.success('取消发布成功');
    setIsCancelConfirmDialogOpen(false);
    void refetchActivityNotification();
  };

  const formDisabled = useMemo(() => {
    if (!id) {
      return false;
    }
    if (activityNotification?.publishStatus !== ActivityPublishStatus.PENDING) {
      return true;
    }
    return false;
  }, [id, activityNotification]);

  const isCanRecall = useMemo(() => {
    if (
      activityNotification?.publishStatus === ActivityPublishStatus.PUBLISHED &&
      recallCountdown > 0
    ) {
      return true;
    }
    return false;
  }, [activityNotification, recallCountdown]);

  const countdown = useMemo(() => {
    if (recallCountdown > 0) {
      const hours = parseInt(
        (recallCountdown % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = parseInt(
        (recallCountdown % (1000 * 60 * 60)) / (1000 * 60)
      );
      const seconds = parseInt((recallCountdown % (1000 * 60)) / 1000);

      return `${hours < 10 ? `0${hours}` : hours}:${minutes < 10 ? `0${minutes}` : minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
    }
  }, [recallCountdown]);

  const handleRecall = async () => {
    if (!id) {
      return;
    }
    const resp = await activityNotificationRecall(Number(id));
    if (resp.status !== 200) {
      messageApi.error('撤回失败: ' + resp.message);
      return;
    }
    messageApi.success('撤回成功');
    void refetchActivityNotification();
  };

  return (
    <div>
      {contextHolder}
      <Form
        form={form}
        style={{ margin: '20px auto 0', maxWidth: 1000 }}
        onFinish={handleSubmit}
        labelAlign="left"
        labelCol={{
          span: 2,
        }}
      >
        <Form.Item>
          <Form.Item
            label="cover"
            name="cover"
            rules={[{ required: true, message: '请上传封面' }]}
          >
            <Form.Item noStyle shouldUpdate={(prev, next) => prev.cover !== next.cover}>
              {({ getFieldValue }) => {
                const coverValue = getFieldValue('cover') || '';
                // 只在初始化时（没有上传新图片）传递 value，避免覆盖用户裁剪上传的新图片
                // 一旦用户上传了新图片，就不再传递 value，让组件使用内部的 croppedPreviewUrl
                return (
                  <div style={{ pointerEvents: formDisabled ? 'none' : 'auto', opacity: formDisabled ? 0.6 : 1 }}>
                    <ImageCropper
                      value={!hasUploadedNewImage ? coverValue : undefined}
                      // aspectRatio={2.95 / 1} // 推荐尺寸的宽高比
                      width={1005}
                      height={512}
                      cropBoxWidth={1005}
                      cropBoxHeight={512}
                      quality={1.0}
                      fixedCropBox={true}
                      autoUpload={true}
                      showCroppedPreview={true}
                      picDesc="上传封面（支持拖拽上传）"
                      fileTypes={['image/png', 'image/jpeg', 'image/jpg', 'image/webp']}
                      maxFileSizeMB={MAX_FILE_SIZE_MB}
                      enablePreview={true}
                      enableDragUpload={true}
                      useActivityUpload={true}
                      // uploadApi={handleImageUpload}
                      onUploadSuccess={handleImageUploadSuccess}
                      onUploadError={(error) => {
                        messageApi.error(error?.message || '上传失败');
                      }}
                    />
                    <div style={{ color: '#999', marginTop: 10 }}>
                      Image format supported: png, jpg, jpeg, webp，File size limit: up to 1 MB，Recommended size: 1005 px × 512 px
                    </div>
                  </div>
                );
              }}
            </Form.Item>
          </Form.Item>
        </Form.Item>
        <Form.Item
          label="活动标题"
          name="title"
          rules={[{ required: true, message: '请输入活动标题' }]}
        >
          <Input
            maxLength={50}
            disabled={formDisabled}
            placeholder="最大50字符"
          />
        </Form.Item>
        <Form.Item label="跳转链接" required>
          <Form.Item style={{ marginBottom: 10 }}>
            <Radio.Group
              value={linkType}
              onChange={(e) => {
                setLinkType(e.target.value as ActivityNotifyType);
                form.setFieldValue('link', '');
              }}
              disabled={formDisabled}
            >
              <Radio value={ActivityNotifyType.LINK}>页面链接</Radio>
              <Radio value={ActivityNotifyType.BROWSER}>外部浏览器</Radio>
              <Radio value={ActivityNotifyType.TAG}>话题详情页</Radio>
              <Radio value={ActivityNotifyType.ARTWORK}>作品详情页</Radio>
              <Radio value={ActivityNotifyType.CONTEST}>比赛详情页</Radio>
              <Radio value={ActivityNotifyType.NEWCOMER_CONTEST}>新人王链接跳转</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            name="link"
            rules={[{ required: true, message: '请输入跳转链接' }]}
          >
            {linkType === ActivityNotifyType.LINK && (
              <Input.TextArea
                maxLength={500}
                rows={4}
                disabled={formDisabled}
                placeholder="请输入页面网址(最大500字符)"
              />
            )}
            {linkType === ActivityNotifyType.BROWSER && (
              <Input
                disabled={formDisabled}
                placeholder="请输入页面网址(最大500字符)"
                style={{ width: '100%' }}
              />
            )}
            {linkType === ActivityNotifyType.TAG && (
              <Input
                disabled={formDisabled}
                placeholder="请输入话题名称"
                style={{ width: '100%' }}
              />
            )}
            {linkType === ActivityNotifyType.ARTWORK && (
              <Input
                disabled={formDisabled}
                placeholder="请输入作品ID"
                style={{ width: '100%' }}
              />
            )}
            {linkType === ActivityNotifyType.CONTEST && (
              <Input
                disabled={formDisabled}
                placeholder="请输入比赛ID"
                style={{ width: '100%' }}
              />
            )}
            {linkType === ActivityNotifyType.NEWCOMER_CONTEST && (
              <Input
                disabled={formDisabled}
                placeholder="请输入新人王ID"
                style={{ width: '100%' }}
              />
            )}
          </Form.Item>
        </Form.Item>
        <Form.Item
          label="推送对象"
          name="pushTargetType"
          rules={[{ required: true, message: '请选择推送对象' }]}
        >
          <Select disabled={formDisabled}>
            {userArr.map((item) => {
              const value = UserEnum[item] as number;
              return (
                <Select.Option value={value} key={value}>
                  {item}
                </Select.Option>
              );
            })}
          </Select>
        </Form.Item>
        <Form.Item
          label="发布时间"
          name="publishTime"
          rules={[
            { required: true, message: '请选择发布时间' },
            {
              validator: (_, value: string) => {
                if (dayjs(value).isBefore(dayjs())) {
                  return Promise.reject(new Error('发布时间不能早于当前时间'));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <DatePicker
            showTime
            placeholder="请选择发布时间(日本时间)"
            style={{ width: '100%' }}
            disabled={formDisabled}
          />
        </Form.Item>
        {!formDisabled && (
          <Form.Item label="发送测试" name="testUser">
            <Flex gap={10}>
              <Input
                maxLength={50}
                onChange={(e) => setTestUser(e.target.value)}
                placeholder="请输入邮箱账号"
              />
              <Button
                type="primary"
                disabled={!testUser}
                loading={isLoading}
                onClick={handleSendTest}
              >
                发送到APP
              </Button>
            </Flex>
          </Form.Item>
        )}
        {!formDisabled && (
          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <Space size="small">
              <Button type="primary" htmlType="submit" loading={isLoading}>
                {id ? '编辑' : '创建'}
              </Button>
              {activityNotification?.publishStatus ===
                ActivityPublishStatus.PENDING && (
                <Button
                  type="primary"
                  danger
                  onClick={() => setIsCancelConfirmDialogOpen(true)}
                >
                  取消发布
                </Button>
              )}
              <Button
                onClick={() => navigate('/activity-mng')}
                disabled={isLoading}
              >
                取消
              </Button>
            </Space>
          </div>
        )}
        {isCanRecall && (
          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <Space>
              <Button type="primary" danger onClick={handleRecall}>
                撤回
              </Button>
            </Space>
            <div style={{ fontSize: 12, color: '#929292', marginTop: 10 }}>
              {countdown} 后不可撤回
            </div>
          </div>
        )}
      </Form>
      <CancelConfirmDialog
        isOpen={isCancelConfirmDialogOpen}
        onOk={handleCancel}
        onCancel={() => setIsCancelConfirmDialogOpen(false)}
      />
    </div>
  );
};

export default ActivityEditorPage;
